import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';

@Component({
  selector: 'app-discussion-sidebar',
  standalone: true,
  imports: [CommonModule, LoginModalComponent],
  templateUrl: './discussion-sidebar.component.html',
  styleUrl: './discussion-sidebar.component.scss',
})
export class DiscussionSidebarComponent implements OnInit, OnChanges {
  @Input() activeTab: string = '';

  /** Personalise tabs that require login; show login modal if not logged in. */
  private readonly PERSONALISE_TABS = ['my-discussions', 'my-bookmarks', 'my-pinned', 'post-submission'];

  private readonly ROUTE_TO_TAB_MAP: Record<string, string> = {
    'getting-started': 'getting-started',
    public: 'public',
    general: 'general',
    feedback: 'feedback',
    announcement: 'announcement',
    'my-discussions': 'my-discussions',
    'my-bookmarks': 'my-bookmarks',
    'my-pinned': 'my-pinned',
    'post-submission': 'post-submission',
  };

  showLoginModal = false;

  private router = inject(Router);
  private keycloak = inject(KeycloakService);
  /** When true, parent controls highlight (e.g. discussion detail after API load). */
  private parentControlsActiveTab = false;

  categories = [
    { label: 'Getting Started', tab: 'getting-started', key: 'getting-started' },
    { label: 'Public', tab: 'public', key: 'public' },
    { label: 'General', tab: 'general', key: 'general' },
    { label: 'Feedback', tab: 'feedback', key: 'feedback' },
    { label: 'Announcement', tab: 'announcement', key: 'announcement' }
  ];

  personalise = [
    { label: 'My Discussions', tab: 'my-discussions', key: 'my-discussions' },
    { label: 'My Bookmarks', tab: 'my-bookmarks', key: 'my-bookmarks' },
    { label: 'My Pinned', tab: 'my-pinned', key: 'my-pinned' },
    { label: 'Post Submission', tab: 'post-submission', key: 'post-submission' }
  ];

  ngOnInit(): void {
    this.syncActiveTabFromRouteIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['activeTab']) return;
    const value = changes['activeTab'].currentValue as string;
    if (value) {
      this.parentControlsActiveTab = true;
      return;
    }
    if (value === '' && !this.parentControlsActiveTab) {
      this.syncActiveTabFromRouteIfNeeded();
    }
  }

  /** Infer tab from URL only for known list routes — never default on discussion detail IDs. */
  private syncActiveTabFromRouteIfNeeded(): void {
    if (this.parentControlsActiveTab || this.activeTab) return;

    const path = this.router.url.split('?')[0];
    const segments = path.split('/').filter(Boolean);
    const discussionsIdx = segments.indexOf('discussions');
    if (discussionsIdx === -1) return;

    const tabSegment = segments[discussionsIdx + 1];
    if (!tabSegment) {
      this.activeTab = 'getting-started';
      return;
    }
    if (tabSegment === 'create-new') {
      this.activeTab = '';
      return;
    }
    const mapped = this.ROUTE_TO_TAB_MAP[tabSegment];
    if (mapped) {
      this.activeTab = mapped;
    }
  }

  async navigateToTab(tab: string): Promise<void> {
    // Personalise items require login: check and show modal if not logged in
    if (this.PERSONALISE_TABS.includes(tab)) {
      try {
        const isLoggedIn = await this.keycloak.isLoggedIn();
        if (!isLoggedIn) {
          this.showLoginModal = true;
          return;
        }
      } catch {
        this.showLoginModal = true;
        return;
      }
    }

    // Map tab to route path
    const routeMap: { [key: string]: string } = {
      'getting-started': '/discussions/getting-started',
      'public': '/discussions/public',
      'general': '/discussions/general',
      'feedback': '/discussions/feedback',
      'announcement': '/discussions/announcement',
      'my-discussions': '/discussions/my-discussions',
      'my-bookmarks': '/discussions/my-bookmarks',
      'my-pinned': '/discussions/my-pinned',
      'post-submission': '/discussions/post-submission'
    };

    const route = routeMap[tab] || '/discussions/getting-started';
    this.router.navigate([route]);
  }

  isActive(item: { key: string }): boolean {
    return this.activeTab === item.key;
  }
}
